import { Button, MenuItem, Select, TextField, Box, Container, Snackbar, IconButton, Modal } from "@mui/material";
import { useState, useEffect, useContext } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheckCircle,
  faSpinner,
  faTimesCircle,
  faClock,
  faHourglassStart,
  faDownload,
  faMagnifyingGlass,
  faCommentDots,
  faTimes,
  faHdd, // Added for disk_changed
} from "@fortawesome/free-solid-svg-icons";
import AuthContext from "../AuthContext";
import { getLogs, getCompanyLogs, getAllUsers, getCompanyDepartment, getCompanyUsers, getCompanyDevices, getAllDevices, getAllDepartments, cancelOperation, verifyOtp, diskChangedLogs } from "../apis/axios";
import { Document, Packer, Paragraph, TextRun, HeadingLevel } from "docx";
import { saveAs } from "file-saver";
import { retryUsbBlocking, retryUsbUnblock, retryDeleteDrive, retryFormatDrive, retryDeleteFileFolder, retryEncryptFileFolder, retryDecryptFileFolder, retryDeleteFileFormat, retryEncryptFileFormat, retryDecryptFileFormat } from "../apis/RetryService";
import certificateUrl from '../assets/certificate.pdf';
import OTPModal from './New folder/OtpModal'
import CertificateView from '../components/common/CertificateView'
// Normalize backend status to match frontend labels
const normalizeStatus = (status) => {
  if (!status) return 'Requested';
  const normalized = status
    .replace(/_/g, ' ')
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
  const validStatuses = ['Completed', 'Started', 'In Progress', 'Failed', 'Queued', 'Cancelled', 'Disk Changed'];
  return validStatuses.includes(normalized) ? normalized : 'Requested';
};

const getIconStylesForStatus = (status) => {
  const normalizedStatus = normalizeStatus(status);
  switch (normalizedStatus) {
    case 'Completed':
      return {
        icon: faCheckCircle,
        iconColor: '#3498DB',
        backgroundColor: '#EAF4FB',
      };
    case 'Started':
      return {
        icon: faHourglassStart,
        iconColor: '#FF7A00',
        backgroundColor: '#FFF3E5',
      };
    case 'In Progress':
      return {
        icon: faSpinner,
        iconColor: '#2ECC71',
        backgroundColor: '#E9F9EF',
      };
    case 'Failed':
      return {
        icon: faTimesCircle,
        iconColor: '#E74C3C',
        backgroundColor: '#FCEDEC',
      };
    case 'Queued':
      return {
        icon: faClock,
        iconColor: '#8E44AD',
        backgroundColor: '#F3EAFB',
      };
    case 'Cancelled':
      return {
        icon: faTimesCircle,
        iconColor: '#FF6B00',
        backgroundColor: '#FFF3E5',
      };
    case 'Disk Changed':
      return {
        icon: faHdd,
        iconColor: '#F39C12', // Orange-yellow for visibility
        backgroundColor: '#FEF5E7', // Light yellow background
      };
    default:
      return {
        icon: faHourglassStart,
        iconColor: '#595C60',
        backgroundColor: '#E5E7EB',
      };
  }
};

const formatActivityType = (type) => {
  if (!type) return "Unknown Activity";
  return type
    .split(/[_/]/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const formatTime = (timeStr) => {
  const date = new Date(timeStr);
  if (isNaN(date.getTime())) {
    return "Invalid Time";
  }
  return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
};

const extractDate = (timeStr) => {
  const date = new Date(timeStr);
  if (isNaN(date.getTime())) {
    return "Unknown Date";
  }
  return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
};

const formatJsonPath = (json, activityType) => {
  try {
    const parsed = JSON.parse(json);
    if (activityType === 'format_drive' && parsed.path) {
      const driveLetter = parsed.path
        .replace(/\\+/g, '')
        .replace(/:/g, '')
        .trim();
      return `Drive Letter: ${driveLetter}`;
    } else if (parsed.path) {
      const cleanPath = parsed.path.replace(/\\+/g, '\\').replace(/^\\+|\\+$/g, '');
      return `Path: ${cleanPath}`;
    }
    return json;
  } catch (e) {
    return json;
  }
};

// Mapping of activity types to retry functions
const retryApiMapping = {
  'usb_blocking': retryUsbBlocking,
  'usb_unblock': retryUsbUnblock,
  'delete_drive': retryDeleteDrive,
  'format_drive': retryFormatDrive,
  'delete_file/folder': retryDeleteFileFolder,
  'encryption': retryEncryptFileFolder,
  'decryption': retryDecryptFileFolder,
  'delete_file_format': retryDeleteFileFormat,
  'encrypt_file_format': retryEncryptFileFormat,
  'decrypt_file_format': retryDecryptFileFormat,
};

export default function ActivityLog() {
  const auth = useContext(AuthContext);
  const [logs, setLogs] = useState([]);
  const [activities, setActivities] = useState([]);
  const [filteredActivities, setFilteredActivities] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  const [filters, setFilters] = useState({
    dateRange: "All Dates",
    type: "All Activities",
    dept: "All Departments",
    device: "All Devices",
    user: "All Users",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [users, setUsers] = useState([]);
  const [devices, setDevices] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [isDropdownDataLoaded, setIsDropdownDataLoaded] = useState(false);
  const [expandedCards, setExpandedCards] = useState({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  // State for password modal
  const [openModal, setOpenModal] = useState(false);
  const [modalMode, setModalMode] = useState('encrypt');
  const [password, setPassword] = useState('');
  const [currentLog, setCurrentLog] = useState(null);
  // State to track retried operations
  const [retriedOperations, setRetriedOperations] = useState(new Set());
  const [startedOperation, setStartedOperation] = useState(new Set())
  const [openCertModal, setOpenCertModal] = useState(false);
  const [openOTPModal, setOpenOTPModal] = useState(false);
  const [otpLog, setOtpLog] = useState(null);

  const handleOpenOTPModal = (item) => {
    console.log(item)
    setOtpLog(item);
    setStartedOperation(prev => new Set(prev).add(item.operationId)); // Add this
    setOpenOTPModal(true);
  };

  // Function to close OTP modal
  const handleVerifyOtp = async (otp) => {
    try {
      const res = await verifyOtp({ operation_id: otpLog.operationId, otp: otp });
      //console.log(res)
      if (res.data.success) {
        setSnackbarMessage('OTP verified successfully. Operation is being processed.');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      } else {
        setSnackbarMessage(res.data.error || 'OTP verification failed. Please try again.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error('OTP verification error:', error);

      // Check if the server returned an error message
      const errorMsg = error.response?.data?.error || 'An unexpected error occurred. Please try again.';

      setSnackbarMessage(errorMsg);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  // Function to handle OTP submission

  useEffect(() => {
    const fetchDropdownData = async () => {
      if (!auth) return;

      try {
        if (auth.userType === 'antima') {
          const [usersRes, devicesRes, departmentsRes] = await Promise.all([
            getAllUsers(),
            getAllDevices(),
            getAllDepartments(),
          ]);
          setUsers(usersRes.data || []);
          setDevices(devicesRes.data || []);
          setDepartments(departmentsRes.data || []);
        } else if (auth.admin) {
          const [usersRes, devicesRes, departmentsRes] = await Promise.all([
            getCompanyUsers({ company_id: auth.admin }),
            getCompanyDevices({ company_id: auth.admin }),
            getCompanyDepartment(auth.admin),
          ]);
          setUsers(usersRes.data || []);
          const uniqueDevices = devicesRes.data
            ? Array.from(new Map(devicesRes.data.map(device => [device.id, device])).values())
            : [];
          setDevices(uniqueDevices);
          setDepartments(departmentsRes.data || []);
        }
        setIsDropdownDataLoaded(true);
      } catch (error) {
        setIsDropdownDataLoaded(true);
      }
    };

    fetchDropdownData();
  }, [auth]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      if (!auth || (auth.userType !== 'antima' && !auth.admin)) {
        setLogs({ status: 'error', message: 'Authentication context is not ready' });
        setActivities([]);
        setFilteredActivities([]);
        setIsLoading(false);
        return;
      }

      const activityTypeMapping = {
        "Delete File/Folder": "delete_file/folder",
        "Delete File Format": "delete_file_format",
        "Delete OS": "delete_os",
        "Encryption": "encryption",
        "Decryption": "decryption",
        "USB Blocking": "usb_blocking",
        "USB Unblock": "usb_unblock",
        "Encrypt File Format": "encrypt_file_format",
        "Decrypt File Format": "decrypt_file_format",
        "Format Drive": "format_drive",
        "Disk Changed": "disk_change",
        "All Activities": "",
      };

      const params = {
        dateRange: filters.dateRange,
        activityType: activityTypeMapping[filters.type] || '',
        department: filters.dept !== "All Departments" ? parseInt(filters.dept) : '',
        device: filters.device !== "All Devices" ? parseInt(filters.device) : '',
        userId: filters.user !== "All Users" ? parseInt(filters.user) : '',
        company_id: auth.admin || '',
        page: page,
      };

      let response;
      if (auth.userType === 'antima') {
        response = await getLogs(params);
        //console.log(response)
      } else {
        response = await getCompanyLogs(params);
        //console.log(response)
      }
      //console.log(res)
      if (response.data && response.data.status === 'error') {
        setLogs(response.data);
        setActivities([]);
        setFilteredActivities([]);
        setIsLoading(false);
        return;
      }

      let logsArray;
      if (response.data && Array.isArray(response.data.data)) {
        logsArray = response.data.data;
      } else if (Array.isArray(response.data)) {
        logsArray = response.data;
      } else {
        logsArray = [];
      }
      // Check for operation IDs with non-Requested statuses
      const operationStatuses = {};
      console.log(operationStatuses)
      logsArray.forEach(log => {
        if (log.operation_id) {
          const normalizedStatus = normalizeStatus(log.status);
          if (!operationStatuses[log.operation_id]) {
            operationStatuses[log.operation_id] = new Set();
          }
          operationStatuses[log.operation_id].add(normalizedStatus);
        }
      });
      setLogs(logsArray);
      setHasMore(logsArray.length > 0);

      const groupedByDate = logsArray.reduce((acc, log) => {
        const date = extractDate(log.time && log.time !== "0000-00-00 00:00:00" ? log.time : log.created_At);
        if (!acc[date]) {
          acc[date] = [];
        }
        const normalizedStatus = normalizeStatus(log.status);
        const activityType = formatActivityType(log.type);
        const rawActivityType = log.type;
        const showResponseText = ['delete_file/folder', 'delete_file_format', 'delete_os', 'decryption', 'decrypt_file_format'].includes(rawActivityType) && normalizedStatus === 'Completed';
        const isDeleteActivity = ['delete_file/folder', 'delete_file_format', 'delete_os'].includes(rawActivityType);
        const hasAnyProgress = operationStatuses[log.operation_id]?.has('Queued') ||
          operationStatuses[log.operation_id]?.has('Started') ||
          operationStatuses[log.operation_id]?.has('In Progress') ||
          operationStatuses[log.operation_id]?.has('Completed') ||
          operationStatuses[log.operation_id]?.has('Failed') ||
          operationStatuses[log.operation_id]?.has('Cancelled');

        const isDiskActivity = ['disk_changed']
        acc[date].push({
          activity: activityType,
          status: normalizedStatus,
          message: log.text || "No description available",
          user: log.username || "Unknown User",
          department: log.department_name || "Unknown Department",
          time: formatTime(log.time && log.time !== "0000-00-00 00:00:00" ? log.time : log.created_At),
          deviceName: log.device_name || "Unknown Device",
          deviceId: log.device_id,
          json: log.json || "No details",
          error: log.json && log.json.includes("error") ? log.json : null,
          operationId: log.operation_id,
          actions: (() => {
            const actions = [];
            if (normalizedStatus === 'Completed' && log.assets && isDeleteActivity) {
              actions.push("Download Certificate");
            }
            if (
              normalizedStatus === 'Queued' &&
              (
                !operationStatuses[log.operation_id] ||
                (
                  !operationStatuses[log.operation_id].has('Started') &&
                  !operationStatuses[log.operation_id].has('Failed')
                )
              )
            ) {
              actions.push("Cancel");
            }
            if (normalizedStatus === 'Failed' && !retriedOperations.has(log.operation_id)) {
              actions.push("Retry");
            }
            if (
              normalizedStatus === 'Requested' &&
              !startedOperation.has(log.operation_id) &&
              !hasAnyProgress

            ) {
              console.log(log)
              console.log(log.operation_id)
              console.log(!startedOperation.has(log.operation_id) &&
                !hasAnyProgress)
              actions.push("Submit OTP");
            }

            return actions;
          })(),

          showResponseText: showResponseText,
          raw: log,
        });
        return acc;
      }, {});

      setActivities(prev => {
        if (page === 1) {
          return Object.keys(groupedByDate).map(date => ({
            date,
            items: groupedByDate[date],
          }));
        }

        const mergedByDate = {
          ...prev.reduce((acc, section) => {
            acc[section.date] = section.items;
            return acc;
          }, {})
        };

        Object.keys(groupedByDate).forEach(date => {
          if (mergedByDate[date]) {
            mergedByDate[date] = [...mergedByDate[date], ...groupedByDate[date]];
          } else {
            mergedByDate[date] = groupedByDate[date];
          }
        });

        const mergedActivities = Object.keys(mergedByDate)
          .map(date => ({
            date,
            items: mergedByDate[date],
          }))
          .sort((a, b) => new Date(b.date) - new Date(a.date));

        return mergedActivities;
      });
    } catch (error) {
      setLogs({ status: 'error', message: error.message || 'Failed to fetch logs' });
      setActivities([]);
      setFilteredActivities([]);
      setHasMore(false);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setPage(1);
    setActivities([]);
    setFilteredActivities([]);
    setHasMore(true);
  }, [filters, auth]);

  useEffect(() => {
    if (!auth || (auth.userType !== 'antima' && !auth.admin) || !isDropdownDataLoaded) {
      return;
    }
    fetchData();
  }, [page, JSON.stringify(filters), auth, isDropdownDataLoaded]);

  // Search functionality
  useEffect(() => {
    if (!searchQuery) {
      setFilteredActivities(activities);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = activities.map(section => ({
      ...section,
      items: section.items.filter(item =>
        item.user.toLowerCase().includes(query) ||
        item.activity.toLowerCase().includes(query) ||
        item.deviceName.toLowerCase().includes(query) ||
        item.department.toLowerCase().includes(query) ||
        item.status.toLowerCase().includes(query) ||
        (item.message && item.message.toLowerCase().includes(query))
      ),
    })).filter(section => section.items.length > 0);

    setFilteredActivities(filtered);
  }, [searchQuery, activities]);

  const dropdownData = [
    {
      label: "Date Range",
      key: "dateRange",
      value: "All Dates",
      options: [
        { label: "All Dates", value: "All Dates" },
        { label: "Today", value: "Today" },
        { label: "Last 7 Days", value: "Last 7 Days" },
        { label: "Last 30 Days", value: "Last 30 Days" },
        { label: "Last 90 Days", value: "Last 90 Days" },
      ],
    },
    {
      label: "Activity Type",
      key: "type",
      value: "All Activities",
      options: [
        { label: "All Activities", value: "All Activities" },
        { label: "Delete File/Folder", value: "Delete File/Folder" },
        { label: "Delete File Format", value: "Delete File Format" },
        { label: "Delete OS", value: "Delete OS" },
        { label: "Encryption", value: "Encryption" },
        { label: "Decryption", value: "Decryption" },
        { label: "USB Blocking", value: "USB Blocking" },
        { label: "USB Unblock", value: "USB Unblock" },
        { label: "Encrypt File Format", value: "Encrypt File Format" },
        { label: "Decrypt File Format", value: "Decrypt File Format" },
        { label: "Format Drive", value: "Format Drive" },
      ],
    },
    {
      label: "Department",
      key: "dept",
      value: "All Departments",
      options: [
        { label: "All Departments", value: "All Departments" },
        ...departments.map(dept => ({
          label: dept.name,
          value: String(dept.id),
        })),
      ],
    },
    {
      label: "Devices",
      key: "device",
      value: "All Devices",
      options: [
        { label: "All Devices", value: "All Devices" },
        ...devices.map(device => ({
          label: device.name,
          value: String(device.id),
        })),
      ],
    },
    {
      label: "Users",
      key: "user",
      value: "All Users",
      options: [
        { label: "All Users", value: "All Users" },
        ...users.map(user => ({
          label: user.name,
          value: String(user.id),
        })),
      ],
    },
  ];

  const handleExportLog = () => {
    const exportDate = new Date().toLocaleString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });

    const doc = new Document({
      sections: [
        {
          children: [
            new Paragraph({
              text: "Activity Log",
              heading: HeadingLevel.HEADING_1,
            }),
            new Paragraph({
              children: [
                new TextRun({
                  text: `Exported on: ${exportDate}`,
                  color: "595C60",
                  size: 20,
                }),
              ],
            }),
            new Paragraph({ text: "" }),
            ...(filteredActivities.length === 0
              ? [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "No activities found.",
                      color: "595C60",
                      size: 20,
                    }),
                  ],
                }),
              ]
              : filteredActivities.flatMap(section => [
                new Paragraph({
                  text: section.date,
                  heading: HeadingLevel.HEADING_2,
                  spacing: { before: 200 },
                }),
                ...section.items.flatMap((item, idx) => {
                  const isExpanded = expandedCards[`${section.date}-${idx}`];
                  const formattedJson = ['delete_file/folder', 'delete_file_format', 'delete_os', 'decryption', 'decrypt_file_format', 'encryption', 'encrypt_file_format', 'format_drive'].includes(item.raw.type)
                    ? formatJsonPath(item.json, item.raw.type)
                    : item.json;

                  const paragraphs = [
                    new Paragraph({
                      children: [
                        new TextRun({
                          text: `${item.activity} - ${item.status}`,
                          bold: true,
                          size: 24,
                        }),
                      ],
                    }),
                    new Paragraph({
                      children: [
                        new TextRun({
                          text: `${item.deviceName} - ${formattedJson}`,
                          color: "595C60",
                          size: 20,
                        }),
                      ],
                    }),
                    new Paragraph({
                      children: [
                        new TextRun({
                          text: `By: ${item.user}    Department: ${item.department}`,
                          color: "595C60",
                          size: 20,
                        }),
                      ],
                    }),
                    new Paragraph({
                      children: [
                        new TextRun({
                          text: `Time: ${item.time}`,
                          color: "595C60",
                          size: 20,
                        }),
                      ],
                    }),
                  ];

                  if (isExpanded && item.showResponseText) {
                    paragraphs.push(
                      new Paragraph({
                        children: [
                          new TextRun({
                            text: `Response Text: ${item.message}`,
                            color: "FF7A00",
                            size: 20,
                          }),
                        ],
                        spacing: { before: 100 },
                      })
                    );
                  }

                  paragraphs.push(new Paragraph({ text: "" }));
                  return paragraphs;
                }),
              ])),
          ],
        },
      ],
    });

    Packer.toBlob(doc).then(blob => {
      saveAs(blob, `activity-log-${new Date().toISOString().split('T')[0]}.docx`);
    });
  };

  const handleLoadMore = () => {
    setPage(prevPage => prevPage + 1);
  };

  const [selectedLog, setSelectedLog] = useState(null); // Add state to track the selected log for CertificateView

  // Updated handleDownloadCertificate function
  const handleDownloadCertificate = (log) => {
    //console.log(log)
    setSelectedLog(log); // Set the selected log to pass to CertificateView
    setOpenCertModal(true); // Open the CertificateView modal
  };

  const handleCancel = async (log) => {
    if (!log.operationId) {
      console.error('No operation_id found for log:', log);
      setSnackbarMessage('Cannot cancel operation: Missing operation ID.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    try {
      const response = await cancelOperation({ operation_id: log.operationId });

      if (response.data.success === true) {
        setSnackbarMessage('Operation cancelled successfully.');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        setPage(1);
        setActivities([]);
        setFilteredActivities([]);
        setHasMore(true);
        fetchData();
      } else {
        setSnackbarMessage('Failed to cancel operation: ' + (response.data.message || 'Unknown error'));
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error('Error cancelling operation:', error);
      setSnackbarMessage('Error cancelling operation: ' + (error.message || 'Unknown error'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const openModalHandler = (mode, log) => {
    setModalMode(mode);
    setCurrentLog(log);
    setOpenModal(true);
  };

  const closeModal = () => {
    setOpenModal(false);
    setPassword('');
    setCurrentLog(null);
  };

  const handleConfirmRetryWithPassword = async () => {
    if (!password) {
      setSnackbarMessage(`${modalMode === 'encrypt' ? 'Encryption' : 'Decryption'} password is required.`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    try {
      const retryFunction = modalMode === 'encrypt' ? retryEncryptFileFolder : retryDecryptFileFolder;
      const response = await retryFunction(currentLog.raw, auth.user, password);

      if (response.success) {
        setRetriedOperations(prev => new Set(prev).add(currentLog.raw.operation_id));

        setSnackbarMessage('Operation started successfully.');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        setPage(1);
        setActivities([]);
        setFilteredActivities([]);
        setHasMore(true);
        fetchData();
      } else {
        setSnackbarMessage('Failed to restart operation: ' + (response.message || 'Unknown error'));
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error('Error restarting operation:', error);
      setSnackbarMessage('Error restarting operation: ' + (error.message || 'Unknown error'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      closeModal();
    }
  };

  const handleRetry = async (log) => {
    const { raw } = log;
    if (!raw.operation_id) {
      console.error('No operation_id found for log:', raw);
      setSnackbarMessage('Cannot retry operation: Missing operation ID.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    const activityType = raw.type;
    const retryFunction = retryApiMapping[activityType];

    if (!retryFunction) {
      setSnackbarMessage(`Retry not supported for activity type: ${formatActivityType(activityType)}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    // For encryption and decryption, prompt for password
    if (activityType === 'encryption' || activityType === 'encrypt_file_format') {
      openModalHandler('encrypt', log);
      return;
    } else if (activityType === 'decryption' || activityType === 'decrypt_file_format') {
      openModalHandler('decrypt', log);
      return;
    }

    try {
      const response = await retryFunction(raw, auth.user);

      if (response.success) {
        setRetriedOperations(prev => new Set(prev).add(raw.operation_id));
        setStartedOperation(prev => new Set(prev).add(raw.operation_id))
        setSnackbarMessage('Operation started successfully.');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        setPage(1);
        setActivities([]);
        setFilteredActivities([]);
        setHasMore(true);
        fetchData();
      } else {
        setSnackbarMessage('Failed to restart operation: ' + (response.message || 'Unknown error'));
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error('Error restarting operation:', error);
      setSnackbarMessage('Error restarting operation: ' + (error.message || 'Unknown error'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const toggleExpandCard = (date, idx) => {
    const key = `${date}-${idx}`;
    setExpandedCards(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const selectStyles = {
    width: '170px',
    minWidth: '150px',
    height: '36px',
    fontFamily: 'Nunito Sans, sans-serif',
    fontSize: '16px',
    color: '#595C60',
    border: '0.6px solid #202224',
    borderRadius: '6px',
    backgroundColor: '#FCFDFD',
    '& .MuiSelect-select': {
      padding: '8px 32px 8px 12px',
      paddingRight: '32px !important',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '&:focus-within': {
      borderColor: '#FF6B00',
    },
    '& .MuiSelect-icon': {
      color: '#202224',
      fontSize: '16px',
    },
  };

  const menuProps = {
    PaperProps: {
      sx: {
        '& .MuiMenuItem-root': {
          fontFamily: 'Nunito Sans, sans-serif',
          fontSize: '16px',
          color: '#202224',
          '&:hover': {
            backgroundColor: '#FFF3E5',
          },
          '&.Mui-selected': {
            backgroundColor: '#FF6B00 !important',
            color: '#FFFFFF',
            '&:hover': {
              backgroundColor: '#FF6B00 !important',
              color: '#FFFFFF',
            },
          },
        },
        '& .MuiMenu-list': {
          padding: 0,
          '& .Mui-selected': {
            backgroundColor: '#FF6B00 !important',
            color: '#FFFFFF',
          },
        },
      },
    },
  };

  return (
    <Box display="flex" sx={{ maxWidth: 'none', justifyContent: 'center' }}>
      <Box flexGrow={1} ml={{ sm: 0 }} sx={{ maxWidth: 'none', alignContent: "center" }}>
        <Container
          className="py-4"
          sx={{
            maxWidth: 'none !important',
            overflowX: 'hidden',
            overflowY: 'hidden',
            paddingLeft: 0,
            paddingRight: 0,
          }}
        >
          <div style={{ margin: '0 auto', width: '75.5vw', maxWidth: '75.5vw' }}>
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4">
              <h1
                style={{
                  fontFamily: 'Nunito Sans, sans-serif',
                  fontSize: '32px',
                  fontWeight: 700,
                  color: '#202224',
                }}
              >
                Activity Log
              </h1>
              <div className="flex flex-wrap gap-3">
                <TextField
                  size="small"
                  placeholder="Search Activities"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <FontAwesomeIcon
                        icon={faMagnifyingGlass}
                        style={{
                          color: '#595C60',
                          marginRight: '8px',
                          fontSize: '16px',
                        }}
                      />
                    ),
                    sx: {
                      fontFamily: 'Nunito Sans, sans-serif',
                      fontSize: '14px',
                      color: '#595C60',
                      backgroundColor: '#FCFDFD',
                      border: '0.6px solid #595C60',
                      borderRadius: '20px',
                      height: '36px',
                      padding: '0 10px',
                      '& .MuiInputBase-input': {
                        padding: '0',
                        height: '36px',
                        lineHeight: '36px',
                      },
                      '& .MuiInputBase-input::placeholder': {
                        color: '#595C60',
                        opacity: 1,
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        border: 'none',
                      },
                    },
                  }}
                  sx={{
                    '& .MuiInputBase-root': {
                      height: '36px',
                    },
                  }}
                />
                <Button
                  variant="contained"
                  className="export-button"
                  onClick={handleExportLog}
                  sx={{
                    width: '120px',
                    height: '36px',
                    backgroundColor: '#FF7A00',
                    borderRadius: '20px',
                    padding: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    border: 'none',
                    boxShadow: '0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12)',
                    fontFamily: 'Nunito Sans, sans-serif',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#fff',
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: '#FF7A00',
                    },
                  }}
                >
                  <FontAwesomeIcon
                    icon={faDownload}
                    style={{
                      height: '10px',
                      width: '10px',
                      color: '#fff',
                    }}
                  />
                  <span>Export Log</span>
                </Button>
              </div>
            </div>

            <div
              className="bg-white rounded-lg shadow"
              style={{
                marginTop: '40px',
                padding: '18px 30px',
              }}
            >
              <div className="flex gap-[30px] flex-wrap">
                <div className="hidden md:flex gap-[30px] flex-wrap">
                  {dropdownData.map(({ label, key, options }) => (
                    <div key={label} className="flex flex-col">
                      <h3
                        style={{
                          fontFamily: 'Nunito Sans, sans-serif',
                          fontSize: '16px',
                          fontWeight: 600,
                          color: '#595C60',
                          marginBottom: '20px',
                        }}
                      >
                        {label}
                      </h3>
                      <Select
                        size="small"
                        value={filters[key]}
                        onChange={(e) =>
                          setFilters({ ...filters, [key]: e.target.value })
                        }
                        sx={selectStyles}
                        MenuProps={menuProps}
                      >
                        {options.map(option => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </div>
                  ))}
                </div>
                <div className="md:hidden flex flex-col gap-[10px]">

                  {dropdownData.map(({ label, key, options }) => (
                    <div key={label} className="flex flex-col flex-1">
                      <h3
                        style={{
                          fontFamily: 'Nunito Sans, sans-serif',
                          fontSize: '16px',
                          fontWeight: '600',
                          color: '#595C60',
                          marginBottom: '20px',
                        }}
                      >
                        {label}
                      </h3>
                      <Select
                        size="small"
                        value={filters[key]}
                        onChange={(e) =>
                          setFilters({ ...filters, [key]: e.target.value })
                        }
                        sx={{
                          ...selectStyles,
                        }}
                        MenuProps={menuProps}
                      >
                        {options.map(option => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </div>
                  ))}

                </div>
              </div>
            </div>

            <div className="mt-6 space-y-8">
              {logs?.status === 'error' ? (
                <p className="text-center text-red-500">{logs.message}</p>
              ) : filteredActivities.length > 0 ? (
                filteredActivities.map((section) => (
                  <div key={section.date}>
                    <h2 className="text-sm text-gray-500 font-medium mb-4">{section.date}</h2>
                    <div className="space-y-4">
                      {section.items.map((item, idx) => {
                        const { icon, iconColor, backgroundColor } = getIconStylesForStatus(item.status);
                        const isExpanded = expandedCards[`${section.date}-${idx}`];
                        const formattedJson = ['delete_file/folder', 'delete_file_format', 'delete_os', 'decryption', 'decrypt_file_format', 'encryption', 'encrypt_file_format', 'format_drive'].includes(item.raw.type)
                          ? formatJsonPath(item.json, item.raw.type)
                          : item.json;
                        return (
                          <div
                            key={idx}
                            className="bg-white p-4 rounded-md shadow-sm flex flex-col"
                          >
                            <div className="flex items-start justify-between flex-col sm:flex-row">
                              <div className="flex items-center gap-3 w-full">
                                <div
                                  style={{
                                    height: '60px',
                                    width: '60px',
                                    borderRadius: '50%',
                                    backgroundColor: backgroundColor,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: '0',
                                  }}
                                >
                                  <FontAwesomeIcon
                                    icon={icon}
                                    style={{
                                      height: '30px',
                                      width: '30px',
                                      color: iconColor,
                                    }}
                                  />
                                </div>
                                <div className="flex-1">
                                  <h6
                                    style={{
                                      fontFamily: 'Nunito Sans, sans-serif',
                                      fontWeight: 700,
                                      color: '#202224',
                                    }}
                                  >
                                    {item.activity} - {item.status}
                                  </h6>
                                  <p
                                    style={{
                                      fontFamily: 'Nunito Sans, sans-serif',
                                      fontWeight: '600',
                                      color: '#595C60',
                                      marginTop: '4px',
                                    }}
                                  >
                                    {item.deviceName} - {formattedJson}
                                  </p>
                                  <div
                                    className="flex text-xs text-gray-500 mt-2 gap-4"
                                    style={{
                                      fontFamily: 'Nunito Sans, sans-serif',
                                      fontSize: '14px',
                                      fontWeight: 600,
                                      color: '#595C60',
                                    }}
                                  >
                                    <span>By: {item.user}</span>
                                    <span>Department: {item.department}</span>
                                  </div>
                                  <div className="flex items-center gap-3 mt-2">
                                    {item.showResponseText && (
                                      <button
                                        onClick={() => toggleExpandCard(section.date, idx)}
                                        style={{
                                          fontFamily: 'Nunito Sans, sans-serif',
                                          fontSize: '14px',
                                          fontWeight: 600,
                                          color: '#FF7A00',
                                        }}
                                        className="text-orange-500 hover:underline"
                                      >
                                        Response Text
                                      </button>
                                    )}
                                    {item.showResponseText && (
                                      <button
                                        onClick={() => handleDownloadCertificate(item)}
                                        style={{
                                          fontFamily: 'Nunito Sans, sans-serif',
                                          fontSize: '14px',
                                          fontWeight: 600,
                                          color: '#FF7A00',
                                          marginLeft: '10px',
                                        }}
                                        className="text-orange-500 hover:underline"
                                      >
                                        Download Certificate
                                      </button>
                                    )}

                                    {item.actions.length > 0 && (
                                      <div className="flex gap-3 text-xs">
                                        {item.actions.map((action, index) => (
                                          <button
                                            key={index}
                                            className="text-orange-500 hover:underline"
                                            onClick={() => {
                                              if (action === "Download Certificate") {
                                                handleDownloadCertificate();
                                              } else if (action === "Cancel") {
                                                handleCancel(item);
                                              } else if (action === "Retry") {
                                                handleRetry(item);
                                              }
                                              else if (action === "Submit OTP") {
                                                handleOpenOTPModal(item);
                                              }
                                            }}
                                            style={{
                                              fontFamily: 'Nunito Sans, sans-serif',
                                              fontSize: '14px',
                                              fontWeight: 600,
                                              color: '#FF7A00',
                                            }}
                                          >
                                            {action}
                                          </button>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div
                                className="text-sm text-gray-400 mt-3 sm:mt-0 sm:self-start"
                                style={{
                                  fontFamily: 'Nunito Sans, sans-serif',
                                  fontSize: '14px',
                                  color: '#595C60',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {item.time}
                              </div>
                            </div>
                            {isExpanded && item.showResponseText && (
                              <div
                                className="mt-3 pl-[76px] flex items-center gap-2"
                                style={{
                                  fontFamily: 'Nunito Sans, sans-serif',
                                  fontSize: '14px',
                                  fontWeight: 600,
                                  color: '#FF7A00',
                                }}
                              >
                                <FontAwesomeIcon
                                  icon={faCommentDots}
                                  style={{
                                    height: '14px',
                                    width: '14px',
                                    color: '#FF7A00',
                                  }}
                                />
                                <span>{item.message}</span>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-center text-gray-500">No activities found.</p>
              )}
            </div>

            <div className="text-center mt-6">
              <button
                className="border px-4 py-2 rounded-md text-sm hover:bg-gray-100 flex items-center justify-content-center gap-2 mx-auto"
                style={{
                  borderColor: '#595C60',
                  color: '#595C60',
                  backgroundColor: '#FCFDFD',
                  fontFamily: 'Nunito Sans, sans-serif',
                }}
                onClick={handleLoadMore}
                disabled={isLoading || !hasMore}
                aria-busy={isLoading}
              >
                {isLoading && (
                  <FontAwesomeIcon
                    icon={faSpinner}
                    className="fa-spin"
                    style={{
                      height: '14px',
                      width: '14px',
                      color: '#595C60',
                    }}
                  />
                )}
                <span>{hasMore ? "Load More Activities" : "No More Activities"}</span>
              </button>
            </div>
          </div>
        </Container>

        {/* Password Modal */}
        <Modal
          open={openModal}
          onClose={closeModal}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1300,
          }}
        >
          <div
            style={{
              backgroundColor: '#F7F7F7',
              padding: '20px 30px',
              borderRadius: '8px',
              width: '90vw',
              maxWidth: '500px',
              position: 'relative',
              boxShadow: '6px 6px 6px #00000040',
            }}
          >
            <div className="flex justify-between items-center mb-4">
              <h1
                style={{
                  fontFamily: 'Nunito Sans, sans-serif',
                  fontSize: '24px',
                  fontWeight: 700,
                  color: '#202224',
                }}
              >
                {modalMode === 'encrypt' ? 'Encryption Settings' : 'Decryption Settings'}
              </h1>
              <FontAwesomeIcon
                icon={faTimes}
                style={{
                  height: '17px',
                  width: '17px',
                  color: '#FF7A00',
                  cursor: 'pointer',
                }}
                onClick={closeModal}
              />
            </div>

            <div className="flex flex-col gap-2">
              <label
                style={{
                  fontFamily: 'Nunito Sans, sans-serif',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#202224',
                }}
              >
                {modalMode === 'encrypt' ? 'Encryption Password*' : 'Decryption Password*'}
              </label>
              <TextField
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={modalMode === 'encrypt' ? 'Enter a strong password' : 'Enter the decryption password'}
                type="password"
                fullWidth
                sx={{
                  fontFamily: 'Nunito Sans, sans-serif',
                  fontSize: '14px',
                  color: '#595C60',
                  backgroundColor: '#FCFCFC',
                  border: '0.6px solid #595C60',
                  borderRadius: '6px',
                  '& .MuiInputBase-input': {
                    height: '40px',
                    padding: '0 12px',
                  },
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: '#FF6B00',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#FF6B00',
                    },
                  },
                }}
              />
              {password && modalMode === 'encrypt' && (
                <p
                  style={{
                    fontFamily: 'Nunito Sans, sans-serif',
                    fontSize: '12px',
                    color: '#B00020',
                    marginTop: '8px',
                  }}
                >
                  ⚠️ Please store your encryption password securely. If forgotten, the encrypted data cannot be recovered or decrypted.
                </p>
              )}
              {password && modalMode === 'decrypt' && (
                <p
                  style={{
                    fontFamily: 'Nunito Sans, sans-serif',
                    fontSize: '12px',
                    color: '#B00020',
                    marginTop: '8px',
                  }}
                >
                  ⚠️ Ensure the password matches the one used for encryption.
                </p>
              )}
            </div>

            <div className="flex justify-end gap-4 mt-4">
              <Button
                onClick={closeModal}
                variant="outlined"
                style={{
                  borderRadius: '5px',
                  textTransform: 'none',
                  fontSize: '14px',
                  padding: '2px 16px',
                  color: '#202224',
                  borderColor: '#202224',
                  fontFamily: 'Nunito Sans, sans-serif',
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmRetryWithPassword}
                variant="contained"
                style={{
                  backgroundColor: '#FF7A00',
                  borderRadius: '5px',
                  textTransform: 'none',
                  fontSize: '14px',
                  padding: '2px 16px',
                  fontFamily: 'Nunito Sans, sans-serif',
                }}
              >
                Confirm
              </Button>
            </div>
          </div>
        </Modal>

        {/* Snackbar for success/error messages */}

      </Box>
      <OTPModal
        open={openOTPModal}
        handleVerifyOtp={handleVerifyOtp}
        handleClose={() => setOpenOTPModal(false)}
      />
      <Container
        sx={{
          maxWidth: 'none !important',
          overflowX: 'hidden',
          paddingLeft: 0,
          paddingRight: 0,
        }}
      >
        {/* ... (previous JSX remains unchanged) */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          sx={{
            '& .MuiSnackbarContent-root': {
              backgroundColor: snackbarSeverity === 'success' ? '#FF7A00' : '#E74C3C',
              color: '#FFFFFF',
              fontFamily: 'Nunito Sans, sans-serif',
              fontSize: '14px',
              borderRadius: '6px',
              boxShadow: '0px 3px 5px rgba(0,0,0,0.2)',
            },
          }}
          message={snackbarMessage}
          action={
            <IconButton size="small" color="inherit" onClick={handleCloseSnackbar}>
              <FontAwesomeIcon icon={faTimes} style={{ height: '14px', width: '14px' }} />
            </IconButton>
          }
        />

        {/* ... (rest of the JSX remains unchanged) */}
      </Container>
      {/* Add CertificateView Modal */}
      <CertificateView
        open={openCertModal}
        handleClose={() => {
          setOpenCertModal(false);
          setSelectedLog(null); // Clear selected log when closing
        }}
        logDetails={selectedLog} // Pass the selected log details
        certificateUrl={certificateUrl} // Pass the certificate URL if needed
      />

    </Box>
  );
}